import React, { useEffect, useState } from "react";
import { Bot, Zap, Ruler, Target } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = { Power: Zap, Size: Ruler, Mission: Target };

/**
 * R2-A2 Compare Highlights — grouped mentor bullets for currently-selected
 * rockets. Rendered under the compare table on ComparePage.
 * Debounces rapid rocket toggles (300ms) and aborts in-flight requests.
 */
export default function R2A2CompareHighlights({ rocketIds }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user || !rocketIds || rocketIds.length === 0) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      setErr(null);
      api.post("/droid/compare", { rocket_ids: rocketIds }, { signal: controller.signal })
        .then(r => setData(r.data))
        .catch(e => {
          if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
          setErr(e?.response?.data?.detail || "R2-A2 unavailable");
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [rocketIds, user]);

  if (!user) return null;
  if (!rocketIds || rocketIds.length === 0) return null;

  return (
    <section
      data-testid="r2a2-highlights"
      className="sat-surface sat-brackets p-4 mt-6 border border-[color:var(--sat-border-bright)]"
    >
      <div className="flex items-center gap-2 mb-4 border-b border-[color:var(--sat-border)] pb-3">
        <span className="relative flex items-center justify-center w-7 h-7 border border-[color:var(--sat-cyan)] sat-cyan">
          <Bot size={14} />
        </span>
        <div>
          <div className="sat-label sat-cyan">R2-A2 MENTOR HIGHLIGHTS</div>
          <div className="text-[10px] tracking-[0.15em] uppercase text-[color:var(--sat-text-dim)]">
            Grouped intel · Power · Size · Mission
          </div>
        </div>
      </div>

      {loading && <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid="r2a2-highlights-loading">R2-A2 · COMPUTING…</div>}
      {err && <div className="sat-label text-red-400" data-testid="r2a2-highlights-error">{err}</div>}

      {data && data.highlights && (
        <>
          {data.overall_summary && (
            <div
              data-testid="r2a2-overall-summary"
              className="border border-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)]/10 p-4 mb-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <Bot size={12} className="sat-cyan" />
                <div className="sat-label sat-cyan">R2-A2 OVERALL READ</div>
              </div>
              <p className="text-[13px] leading-relaxed text-[color:var(--sat-text)]">
                {data.overall_summary}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="r2a2-highlights-grid">
            {["Power", "Size", "Mission"].map(group => {
              const Icon = ICONS[group];
              const rows = data.highlights[group] || [];
              const insight = (data.insights || {})[group];
              return (
                <div key={group} className="border border-[color:var(--sat-border)] bg-black/40 p-3" data-testid={`r2a2-group-${group.toLowerCase()}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={12} className="sat-cyan" />
                    <div className="sat-label sat-cyan">{group}</div>
                  </div>
                  {insight && (
                    <p
                      data-testid={`r2a2-insight-${group.toLowerCase()}`}
                      className="text-[12px] leading-relaxed text-[color:var(--sat-text)] mb-3 pb-3 border-b border-[color:var(--sat-border)]"
                    >
                      {insight}
                    </p>
                  )}
                  <div className="space-y-3">
                    {rows.map((row, i) => (
                      <div key={i}>
                        <div className="font-display font-bold text-[13px] leading-tight mb-1">
                          {row.rocket}
                        </div>
                        <ul className="space-y-0.5 font-mono text-[11px] text-[color:var(--sat-text-dim)]">
                          {(row.bullets || []).map((b, k) => (
                            <li key={k} className="flex gap-1.5">
                              <span className="sat-cyan flex-shrink-0">›</span><span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
